import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from '../lib/store';
import { Icon, type IconName } from '../components/Icon';
import { Cover } from '../components/Cover';
import { IconBtn, TopBar, Progress } from '../components/ui';
import { getChapters, getDB, putChapters, putBook, computeProgressFraction, type BookChapter, type LocalHighlight } from '../lib/db';
import { parseBook } from '../lib/parsers';
import { downloadBookOffline, deleteBookAudio, getBookAudioStats, estimateStorage, type DownloadProgress } from '../lib/offline-audio';
import { useStore as useStoreVoice } from '../lib/store';
import { pullProgress } from '../lib/sync';

const MiniStat = ({ icon, label, value }: { icon: IconName; label: string; value: string | number }) => (
  <div className="rounded-xl bg-paper dark:bg-night-800 shadow-soft p-3">
    <div className="flex items-center gap-1.5 text-ink-500">
      <Icon name={icon} className="w-3.5 h-3.5" />
      <span className="text-[10px] uppercase tracking-wider">{label}</span>
    </div>
    <div className="font-display text-[24px] leading-none mt-1.5">{value}</div>
  </div>
);

export function BookDetail() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const book = useStore((s) => s.books.find((b) => b.id === id));
  const refreshBooks = useStore((s) => s.refreshBooks);

  const currentVoice = useStoreVoice((s) => s.voice);
  const [chapters, setChapters] = useState<BookChapter[]>([]);
  const [counts, setCounts] = useState({ highlights: 0, learnings: 0, quotes: 0 });
  const [hasBlob, setHasBlob] = useState(false);
  const [scanning, setScanning] = useState<{ msg: string; pct: number } | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [dl, setDl] = useState<DownloadProgress | null>(null);
  const [audioStats, setAudioStats] = useState({ count: 0, bytes: 0 });
  const [storage, setStorage] = useState({ usage: 0, quota: 0, pct: 0 });
  const [dlActive, setDlActive] = useState(false);

  useEffect(() => {
    if (!book) return;
    getChapters(book.id).then(setChapters);
    (async () => {
      const db = await getDB();
      const all = (await db.getAll('highlights')) as LocalHighlight[];
      const mine = all.filter((h) => h.book_id === book.id);
      setCounts({
        highlights: mine.length,
        learnings: mine.filter((h) => h.kind === 'learning').length,
        quotes: mine.filter((h) => h.kind === 'quote').length,
      });
      const f = await db.get('files', book.id);
      setHasBlob(!!f);
      setAudioStats(await getBookAudioStats(book.id));
      setStorage(await estimateStorage());

      // Pull authoritative progress from Supabase and sync local if newer.
      // Guarantees the Continue button + progress bar reflect the latest
      // position regardless of which device saved it.
      const localTs = book.position?.ts ?? 0;
      const remote = await pullProgress(book.id, localTs).catch(() => null);
      if (remote) {
        const fresh = (await db.get('books', book.id)) || book;
        const progress = computeProgressFraction(fresh.chapter_words, remote.ch, remote.scroll);
        await putBook({
          ...fresh,
          position: { ch: remote.ch, sent: remote.sent, scroll: remote.scroll, ts: remote.ts },
          progress,
        });
        await refreshBooks();
      }
    })();
  }, [book?.id]);

  const rescan = async () => {
    if (!book) return;
    setScanError(null);
    try {
      const db = await getDB();
      const rec = await db.get('files', book.id);
      if (!rec || !rec.blob) {
        setScanError('No file stored for this book. Re-upload to add.');
        return;
      }
      const file = new File([rec.blob], book.title || 'book', { type: rec.blob.type });
      setScanning({ msg: 'Starting...', pct: 0 });
      const parsed = await parseBook(file, (p) => setScanning({ msg: p.phase, pct: p.pct ?? 0 }));
      setScanning({ msg: 'Saving...', pct: 95 });
      await putChapters(book.id, parsed.chapters);
      // Update book metadata (title/author/pages/chapters/cover) in case they changed
      await db.put('books', {
        ...book,
        title: parsed.title || book.title,
        author: parsed.author || book.author,
        pages: parsed.pages,
        chapters: parsed.chapters.length,
        cover_data: parsed.coverDataUrl ?? book.cover_data ?? null,
        kind: parsed.kind,
      });
      await refreshBooks();
      const cs = await getChapters(book.id);
      setChapters(cs);
      setScanning(null);
    } catch (e: any) {
      console.error('[rescan] failed', e);
      setScanError(e?.message || String(e));
      setScanning(null);
    }
  };

  const togglePin = async () => {
    if (!book) return;
    const db = await getDB();
    const nextPinned = !book.pinned;
    await db.put('books', { ...book, pinned: nextPinned });
    await refreshBooks();
    if (nextPinned) {
      // Kick off offline download in background. isActive gate lets us
      // cancel by toggling off or navigating away.
      setDl({ status: 'init', done: 0, total: 0 });
      setDlActive(true);
      const active = { v: true };
      (async () => {
        await downloadBookOffline(book, currentVoice, (p) => setDl(p), () => active.v);
        setAudioStats(await getBookAudioStats(book.id));
        setStorage(await estimateStorage());
        setDlActive(false);
      })();
      // Store cancel hook on window so toggle-off can stop mid-download
      (window as any).__doomCancelOfflineDl = () => { active.v = false; };
    } else {
      if ((window as any).__doomCancelOfflineDl) (window as any).__doomCancelOfflineDl();
      const removed = await deleteBookAudio(book.id);
      setDl({ status: 'done', done: removed, total: removed });
      setTimeout(() => setDl(null), 1500);
      setAudioStats(await getBookAudioStats(book.id));
      setStorage(await estimateStorage());
      setDlActive(false);
    }
  };

  const fmtBytes = (n: number) => {
    if (!n) return '0 B';
    const u = ['B', 'KB', 'MB', 'GB'];
    let i = 0, v = n;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
  };

  if (!book) return <div className="p-8">book not found</div>;

  const openAtChapter = (i: number) => nav(`/read/${book.id}?ch=${i}`);
  const currentChapter = Math.max(0, Math.min(chapters.length - 1, Math.round(book.progress * Math.max(1, chapters.length - 1))));

  return (
    <div className="flex flex-col h-full safe-top">
      <TopBar left={<IconBtn name="back" onClick={() => nav(-1)} />} title="" />
      <div className="flex-1 overflow-y-auto no-scrollbar pb-8">
        <div className="px-8 pt-4 flex justify-center">
          <Cover kind={book.cover_kind} dataUrl={book.cover_data} className="w-[180px] h-[264px] rounded-xl" />
        </div>
        <div className="px-8 mt-6 text-center">
          <h1 className="font-display text-[28px] leading-[1.1] tracking-tight">{book.title}</h1>
          <p className="text-[13px] text-ink-500 mt-1">{book.author}</p>
          <div className="flex items-center justify-center gap-3 mt-3 text-[11px] text-ink-500 flex-wrap">
            <span className="flex items-center gap-1"><Icon name="book" className="w-3 h-3" />{book.pages} pages</span>
            <span className="w-1 h-1 rounded-full bg-ink-300" />
            <span>{chapters.length || book.chapters} chapters</span>
            <span className="w-1 h-1 rounded-full bg-ink-300" />
            <span>{Math.round(book.progress * 100)}% read</span>
            {book.kind && book.kind !== 'seed' && (
              <>
                <span className="w-1 h-1 rounded-full bg-ink-300" />
                <span className="uppercase">{book.kind}</span>
              </>
            )}
          </div>
        </div>

        <div className="px-5 mt-6">
          <Progress value={book.progress} className="h-1.5" />
        </div>

        <div className="px-5 mt-5 grid grid-cols-2 gap-2.5">
          <button onClick={() => nav(`/read/${book.id}`)} className="h-12 rounded-full bg-ink-900 dark:bg-cream-100 text-cream-50 dark:text-ink-900 font-medium text-[14px] flex items-center justify-center gap-2">
            <Icon name="book" className="w-4 h-4" />Continue
          </button>
          <button onClick={() => nav(`/read/${book.id}`)} className="h-12 rounded-full bg-paper dark:bg-night-800 shadow-soft font-medium text-[14px] flex items-center justify-center gap-2">
            <Icon name="headphones" className="w-4 h-4" />Listen
          </button>
        </div>

        <div className="px-5 mt-4">
          <button onClick={togglePin} className="w-full rounded-2xl p-4 bg-paper dark:bg-night-800 shadow-soft flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${book.pinned ? 'bg-forest-500/10 text-forest-500' : 'bg-black/5 dark:bg-white/10 text-ink-500'}`}>
              <Icon name="pin" className="w-4 h-4" strokeWidth={1.6} />
            </div>
            <div className="flex-1 text-left">
              <div className="text-[13px] font-medium">Pinned for offline</div>
              <div className="text-[11px] text-ink-500">
                {dlActive && dl?.status === 'downloading'
                  ? `Downloading ${dl.done}/${dl.total}${dl.currentChapterTitle ? ' · ' + dl.currentChapterTitle : ''}`
                  : book.pinned
                    ? `${audioStats.count} sentences cached · ${fmtBytes(audioStats.bytes)}`
                    : 'Not pinned'}
              </div>
            </div>
            <div className={`w-10 h-6 rounded-full relative transition ${book.pinned ? 'bg-forest-500' : 'bg-black/10 dark:bg-white/10'}`}>
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-cream-50 shadow transition-all ${book.pinned ? 'right-0.5' : 'left-0.5'}`} />
            </div>
          </button>

          {dl && dl.status === 'downloading' && dl.total > 0 && (
            <div className="mt-2">
              <Progress value={dl.total ? dl.done / dl.total : 0} className="h-1" />
              <div className="mt-1 text-[10px] text-ink-500 tabular-nums">{Math.round((dl.total ? dl.done / dl.total : 0) * 100)}% · {dl.done}/{dl.total}</div>
            </div>
          )}
          {dl && dl.status === 'error' && (
            <div className="mt-2 text-[11px] text-coral">{dl.error}</div>
          )}

          {storage.quota > 0 && (
            <div className="mt-3 rounded-2xl bg-paper dark:bg-night-800 shadow-soft p-3">
              <div className="flex items-center justify-between text-[11px] text-ink-500 mb-1.5">
                <span>Device storage</span>
                <span className="tabular-nums">{fmtBytes(storage.usage)} / {fmtBytes(storage.quota)}</span>
              </div>
              <Progress value={storage.pct} className="h-1" />
              <div className="mt-1 text-[10px] text-ink-500 tabular-nums">{Math.round(storage.pct * 100)}% used</div>
            </div>
          )}
        </div>

        {book.blurb && (
          <p className="px-6 mt-6 font-serif text-[15px] leading-[1.6] text-ink-700 dark:text-ink-300">{book.blurb}</p>
        )}

        <div className="px-5 mt-6 grid grid-cols-3 gap-2">
          <MiniStat icon="highlight" label="Highlights" value={counts.highlights} />
          <MiniStat icon="sparkle" label="Learnings" value={counts.learnings} />
          <MiniStat icon="bookmark" label="Quotes" value={counts.quotes} />
        </div>

        {chapters.length === 0 && book.kind && book.kind !== 'seed' && hasBlob && (
          <div className="px-5 mt-6">
            <div className="rounded-2xl p-4 bg-coral/10 border border-coral/30">
              <div className="text-[13px] font-medium text-ink-900 dark:text-cream-100">Book not parsed yet</div>
              <p className="text-[11px] text-ink-500 mt-1">The file is stored but chapter extraction didn't run. Tap rescan to parse it now.</p>
              <button onClick={rescan} disabled={!!scanning} className="mt-3 h-10 px-4 rounded-full bg-ink-900 dark:bg-cream-100 text-cream-50 dark:text-ink-900 text-[12px] font-medium disabled:opacity-50">
                {scanning ? `${scanning.msg} · ${scanning.pct}%` : 'Rescan file'}
              </button>
              {scanError && <p className="text-[11px] text-coral mt-2 break-words">{scanError}</p>}
            </div>
          </div>
        )}

        {chapters.length > 0 ? (
          <div className="px-5 mt-7">
            <div className="flex items-end justify-between mb-3">
              <h3 className="font-display text-[20px]">Chapters</h3>
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-ink-500 tabular-nums">{chapters.length}</span>
                {hasBlob && book.kind !== 'seed' && (
                  <button onClick={rescan} disabled={!!scanning} className="text-[11px] text-forest-500 font-medium disabled:opacity-50">
                    {scanning ? `${scanning.pct}%` : 'Rescan'}
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              {chapters.map((c, i) => {
                const isDone = i < currentChapter;
                const isCurrent = i === currentChapter;
                return (
                  <button
                    key={c.idx}
                    onClick={() => openAtChapter(i)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition ${isCurrent ? 'bg-paper dark:bg-night-800 shadow-soft' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold tabular-nums shrink-0 ${isDone ? 'bg-forest-500 text-cream-50' : isCurrent ? 'bg-ink-900 text-cream-50 dark:bg-cream-100 dark:text-ink-900' : 'bg-black/5 dark:bg-white/10 text-ink-500'}`}>
                      {isDone ? <Icon name="check" className="w-3 h-3" /> : i + 1}
                    </div>
                    <div className={`flex-1 min-w-0 text-[14px] truncate ${isCurrent ? 'font-medium' : ''} ${isDone ? 'text-ink-500' : ''}`}>{c.title || `Chapter ${i + 1}`}</div>
                    <Icon name="chevRight" className="w-4 h-4 text-ink-400 shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="px-5 mt-7">
            <div className="rounded-2xl p-4 bg-paper dark:bg-night-800 shadow-soft">
              <div className="text-[13px] font-medium mb-1">No chapters indexed yet</div>
              <p className="text-[11px] text-ink-500">This is a seed book or chapter data wasn't parsed. Upload a PDF/EPUB to get chapter navigation.</p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
